import { Button, Checkbox, Form, Input, Modal, Select } from 'antd';
import React, { useState, useEffect } from 'react';
import SearchAddress from '../../Utils/SearchAddress';
import useAuthRequest from '../../../services/requests.service';
import { useAuth } from '../../../hooks/AuthProvider';
import { Bounce, toast } from 'react-toastify';
import { useAppData } from '../../../hooks/useAppData';
import { useCreatePage } from '../../../hooks/usePages';

const { TextArea } = Input;

const CreatePageModal = ({ isModalOpen, setIsModalOpen }) => {
  const [step, setStep] = useState(1);
  const { data: appData, isLoading: appDataLoading, error: appDataError } = useAppData();
  const createPageMutation = useCreatePage();

  const request = useAuthRequest();

  const [typeCompany, setTypeCompany] = useState(0);
  const [form] = Form.useForm();
  const auth = useAuth();

  const [formData, setFormData] = useState({});

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const afterClose = () => {
    setStep(1);
    setTypeCompany(0);
    form.resetFields();
  };

  const handleAddressChange = (fieldName, place) => {
    setFormData((prevData) => ({
        ...prevData,
        [fieldName]: place
    }));
    form.setFieldsValue({
        [fieldName]: place
    });
  };

  const handleSubmitEvent = (values) => {
    const newFormData = { ...formData, ...values };
    console.log(newFormData)
    setFormData(newFormData);
    setStep(step + 1);
  };

  const handleSubmit = async (values) => {
    let data = { ...formData, ...values };
  
    data.typeCompany = "inde";
  
    if (data.isPartner) {
      data.typePage = "partner";
    } else {
      data.typePage = "company";
    }
  
    try {
      await createPageMutation.mutateAsync(data);
      setIsModalOpen(false);

      toast.success('Page créée avec succès!', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'light',
        transition: Bounce,
      });
  
    }catch (e) {
      console.error('Error in handleSubmit:', e);
      toast.error(e.message || 'Une erreur est survenue.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'light',
        transition: Bounce,
      });
    }
  };

  useEffect(() => {
    form.setFieldsValue(formData);
  }, [formData, form]);

  const styles = {
    body: {
      height: "85vh",
      maxHeight: "85vh",
      overflowY: "hidden",
      padding: "0",
      borderRadius: "12px",
    },
    header: {
      borderBottom: "1px solid #f0f0f0",
      padding: "20px 24px",
      margin: 0,
    },
    content: {
      padding: "0",
      height: "100%",
      borderRadius: "12px",
    },
  };

  // Loading state pour les données d'application
  if (appDataLoading) {
    return (
      isModalOpen && <Modal
        centered
        styles={styles}
        footer={null}
        open={isModalOpen}
        onCancel={handleCancel}
        afterClose={afterClose}
        width={600}
      >
        <div className='flex flex-col justify-center items-center w-full h-full min-h-[400px]'>
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto"></div>
            <p className="text-gray-600 text-lg font-medium">Chargement des données...</p>
          </div>
        </div>
      </Modal>
    );
  }

  // Error state pour les données d'application
  if (appDataError) {
    return (
      isModalOpen && <Modal
        centered
        styles={styles}
        footer={null}
        open={isModalOpen}
        onCancel={handleCancel}
        afterClose={afterClose}
        width={600}
      >
        <div className='flex flex-col justify-center items-center w-full h-full min-h-[400px]'>
          <div className="text-center space-y-6">
            <div className="text-red-500 text-6xl">⚠️</div>
            <div>
              <h3 className="text-red-600 text-xl font-semibold mb-2">Erreur lors du chargement</h3>
              <p className="text-gray-600 mb-6">Impossible de charger les données nécessaires</p>
            </div>
            <Button 
              type="primary" 
              size="large"
              onClick={() => window.location.reload()}
              className="px-8"
            >
              Réessayer
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const renderStepHeader = (stepNumber, title, description) => (
    <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
          {title}
        </h3>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">Étape {stepNumber}/3</span>
          <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className={`h-full bg-blue-600 rounded-full transition-all duration-300`} 
                 style={{ width: `${(stepNumber / 3) * 100}%` }}></div>
          </div>
        </div>
      </div>
      <p className="text-gray-600 dark:text-gray-300">
        {description}
      </p>
    </div>
  );

  const renderFormContainer = (children) => (
    <div className="flex-1 p-8 overflow-y-auto">
      <div className="max-w-lg mx-auto">
        {children}
      </div>
    </div>
  );

  return (
    isModalOpen && <Modal
      centered
      styles={styles}
      footer={null}
      open={isModalOpen}
      onCancel={handleCancel}
      afterClose={afterClose}
      width={800}
      className="create-page-modal"
    >
      <div className='flex flex-col w-full h-full bg-white dark:bg-gray-800'>

        {step === 1 && (
          <>
            {renderStepHeader(
              1, 
              "Informations de l'entreprise", 
              "Commençons par les informations de base de votre entreprise"
            )}
            {renderFormContainer(
              <Form
                layout="vertical"
                form={form}
                initialValues={formData}
                onFinish={handleSubmitEvent}
                className="space-y-6"
              >
                {auth?.user?.user?.role === "admin" && (
                  <Form.Item
                    name="isPartner"
                    hasFeedback
                    className="mb-6"
                  >
                    <Checkbox 
                      name="isPartner" 
                      checked={formData.isPartner || false} 
                      onChange={(e) =>
                        setFormData({ ...formData, isPartner: e.target.checked })
                      }
                      className="text-base"
                    >
                      Je suis une page partenaire
                    </Checkbox>
                  </Form.Item>
                )}
                
                <Form.Item
                  label={typeCompany === 0 ? "Dénomination sociale de l'entreprise" : "Dénomination sociale de la maison mère*"}
                  name="registeredName"
                  hasFeedback
                  rules={[
                    { required: true, message: 'Entrez votre dénomination sociale!' },
                    { max: 120, message: 'La dénomination sociale ne peut pas dépasser 120 caractères!' }
                  ]}
                >
                  <Input name="registeredName" size="large" maxLength={120} />
                </Form.Item>

                <Form.Item
                  label="N° SIRET"
                  name="siret"
                  rules={[
                    { required: true, message: 'Entrez votre SIRET!' },
                    {
                      validator: (_, value) =>
                        value && /^\d{14}$/.test(value)
                          ? Promise.resolve()
                          : Promise.reject(new Error('SIRET doit contenir exactement 14 chiffres!'))
                    }
                  ]}
                  hasFeedback
                >
                  <Input name="siret" size='large' />
                </Form.Item>

                <Form.Item label="Adresse postale" name="postalAddress">
                  <SearchAddress 
                    onAddressChange={handleAddressChange} 
                    address={formData.postalAddress ? formData.postalAddress : null} 
                    fieldName="postalAddress" 
                  />
                </Form.Item>
                
                <Form.Item className="flex justify-end mt-8">
                  <Button 
                    size="large" 
                    type="primary" 
                    htmlType="submit" 
                    loading={createPageMutation.isPending}
                    className="px-6"
                  >
                    Suivant
                  </Button>
                </Form.Item>
              </Form>
            )}
          </>
        )}

        {step === 2 && (
          <>
            {renderStepHeader(
              2, 
              "Informations de l'établissement", 
              "Maintenant, détaillons les informations de votre établissement"
            )}
            {renderFormContainer(
              <Form
                form={form}
                initialValues={formData}
                onFinish={handleSubmitEvent}
                layout="vertical"
                className="space-y-6"
              >
                <Form.Item
                  label="Nom d'établissement"
                  name="name"
                  hasFeedback
                  rules={[
                    { required: true, message: "Entrez le nom de l'entreprise!" },
                    { max: 120, message: 'Le nom de l\'entreprise ne peut pas dépasser 120 caractères!' }
                  ]}
                >
                  <Input name="name" size="large" maxLength={120} />
                </Form.Item>

                <Form.Item 
                  name="sector" 
                  label="Type d'établissement" 
                  rules={[{ required: true, message: 'Sélectionnez le type!' }]}
                >
                  <Select 
                    size="large" 
                    placeholder="Sélectionnez type" 
                    dropdownStyle={{ zIndex: "100000" }}
                  >
                    {appData?.companyTypes?.map((sector) => (
                      <Select.Option key={sector.id} value={sector.id}>
                        {sector.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label="Adresse de l'établissement" name="address">
                  <SearchAddress 
                    onAddressChange={handleAddressChange} 
                    address={formData.address ? formData.address : null} 
                    fieldName="address" 
                  />
                </Form.Item>

                <Form.Item
                  label="Email"
                  name="email"
                  hasFeedback
                  rules={[
                    { type: 'email', message: "Entrez une adresse email valide" },
                    { max: 120, message: 'L\'email ne peut pas dépasser 120 caractères!' }
                  ]}
                >
                  <Input name="email" size="large" maxLength={120} />
                </Form.Item>

                <Form.Item
                  label="Téléphone"
                  name="phone"
                  rules={[
                    { pattern: /^0\d{9}$/, message: 'Le numéro de téléphone doit comporter 10 chiffres et commencer par 0' }
                  ]}
                  hasFeedback
                >
                  <Input name="phone" size="large" />
                </Form.Item>

                <Form.Item
                  label="Site web"
                  name="website"
                  rules={[
                    { message: "Entrez le site web" },
                    { max: 120, message: 'L\'adresse du site ne peut pas dépasser 120 caractères!' }
                  ]}
                >
                  <Input name="website" size="large" maxLength={120} />
                </Form.Item>

                <Form.Item className="flex justify-end mt-8">
                  <div className="flex space-x-4">
                    <Button 
                      size="large" 
                      onClick={() => setStep(1)}
                    >
                      Précédent
                    </Button>
                    <Button 
                      size="large" 
                      type="primary" 
                      htmlType="submit"
                      className="px-6"
                    >
                      Suivant
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            )}
          </>
        )}

        {step === 3 && (
          <>
            {renderStepHeader(
              3, 
              "Finalisation", 
              "Ajoutons les derniers détails pour finaliser votre page"
            )}
            {renderFormContainer(
              <Form
                form={form}
                initialValues={formData}
                onFinish={handleSubmit}
                layout="vertical"
                className="space-y-6"
              >
                <Form.Item
                  label="Spécialités"
                  name="tags"
                >
                  <Select
                    mode="tags"
                    placeholder="Entrez vos spécialités"
                    size='large'
                    className="w-full"
                  />
                </Form.Item>

                <Form.Item
                  label="Description"
                  name="description"
                  rules={[
                    { required: true, message: "Entrez une description!" },
                    { max: 1000, message: 'La description ne peut pas dépasser 1000 caractères!' }
                  ]}
                  hasFeedback
                >
                  <TextArea 
                    size='large' 
                    maxLength={1000} 
                    showCount 
                    rows={6}
                    placeholder="Décrivez votre entreprise, vos services, votre expertise..."
                  />
                </Form.Item>

                <Form.Item className="flex justify-end mt-8">
                  <div className="flex space-x-4">
                    <Button 
                      size="large" 
                      onClick={() => setStep(2)}
                      disabled={createPageMutation.isPending}
                    >
                      Précédent
                    </Button>
                    <Button 
                      size="large" 
                      type="primary" 
                      htmlType="submit"
                      loading={createPageMutation.isPending}
                      className="px-6"
                    >
                      Créer ma page
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            )}
          </>
        )}

      </div>
    </Modal>
  );
};

export default CreatePageModal;
